"use client";

import {
  CopilotChatPanel,
  CopilotProvider,
  createCopilotRegistry,
  useCopilotChat,
  useConversationId,
} from "@zapier/ai-toolkit/client";
import { Suspense, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { Text } from "@zapier/design-system";

import AuthedPage from "../components/AuthedPage";
import FooterSlot from "../components/FooterSlot";
import { getTicketCopilotBaseUrl } from "../utils/urls";
import { confirmLokiIssueTool } from "./client-tools";
import { createProfileFromSession } from "../utils/profile";
import {
  getStoredConversationId,
  storeConversationId,
} from "../utils/conversationStorage";
import { COPILOT_ID } from "@utils/constants";

const getInitialMessages = (ticketId: string | null) => {
  if (ticketId) {
    return [
      {
        id: "welcome-message",
        role: "assistant" as const,
        content: `🎫 Welcome to Ticket Copilot! I'm your AI-powered support assistant.

I can help you with these tasks:

**📋 Look up ticket details**
- "Look up this ticket"

**⚡ Look up Zap details** (🆕)
- "What does Zap 106127980 do?"
- "Look up the Zap the customer provided"

**🔍 Search help center articles**
- "Can I use my own Claude API key with Zapier Chatbots?"

**📚 Search internal KB for TSS documentation**
- "What does the internal KB say about verifying a non-profit from Singapore?"

**🐛 Search Loki for bugs and feature requests**
- "Are there any Shopify feature requests for creating a discount code?"

**📓 Search Rover for app-specific notes and workarounds**
- "Search Rover for Mailchimp error 'Your API key may be invalid'"

**✍️ Generate professional support replies**
- "Generate a reply for the ticket"

**📝 Create internal notes (TLDRs)**
- "Create an internal note for this ticket"
- "Create a TLDR for the ticket"

**🐛 Create Loki issues**
- "Create a bug report for Shopify webhook validation issue"
- "Create a feature request for Slack to add a Create List action"

**🧠 Search Support Brain**
- "Search Support Brain for the issue in this ticket"
- "Find Support Brain entries for a 404 error related to Google Sheets New Row trigger"

**🔄 Search workflow recommendations** (🆕)
- "How can I automate lead follow-ups with HubSpot?"
- "What workflows connect Slack and Google Sheets?"

**💡 Search automation use cases** (🆕)
- "How can I improve my marketing operations?"
`,
      },
    ];
  }

  return [
    {
      id: "welcome-message",
      role: "assistant" as const,
      content: `🎫 Welcome to **Ticket Copilot**! I'm your AI-powered support assistant.

I can help you with these tasks:

**📋 Look up ticket details**
- "Look up ticket 2423822"

**⚡ Look up Zap details** (🆕)
- "What does Zap 106127980 do?"
- "Look up the Zap the customer provided"

**🔍 Search help center articles**
- "Can I use my own Claude API key with Zapier Chatbots?"

**📚 Search internal KB for TSS documentation**
- "What does the internal KB say about verifying a non-profit from Singapore?"

**🐛 Search Loki for bugs and feature requests**
- "Are there any Shopify feature requests for creating a discount code?"

**📓 Search Rover for app-specific notes and workarounds**
- "Search Rover for Mailchimp error 'Your API key may be invalid'"

**✍️ Generate professional support replies**
- "Generate a reply for the ticket"

**📝 Create internal notes (TLDRs)**
- "Create an internal note for the ticket"
- "Create a TLDR for the ticket"

**🐛 Create Loki issues**
- "Create a bug report for Shopify webhook validation issue"
- "Create a feature request for Slack to add a Create List action"

**🔄 Search workflow recommendations** (🆕)
- "How can I automate lead follow-ups with HubSpot?"
- "What workflows connect Slack and Google Sheets?"

**💡 Search automation use cases** (🆕)
- "How can I improve my marketing operations?"`,
    },
  ];
};

function TicketCopilotInner({ ticketId }: { ticketId: string | null }) {
  // Auto-persist conversationId to localStorage when it's generated
  const { conversationId } = useCopilotChat();

  useEffect(() => {
    if (ticketId && conversationId) {
      const storedConversationId = getStoredConversationId(ticketId);
      if (conversationId !== storedConversationId) {
        storeConversationId(ticketId, conversationId);
      }
    }
  }, [ticketId, conversationId]);

  const placeholder = ticketId
    ? "Ask about this ticket or request a reply (e.g., 'Show me this ticket' or 'Generate reply for the ticket')..."
    : "Ask about a ticket or request a reply (e.g., 'Show ticket 12345' or 'Generate reply for ticket 67890')...";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          maxWidth: "720px",
          margin: "0 auto",
          padding: "24px",
          gap: "16px",
          width: "100%",
          minHeight: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <CopilotChatPanel
            placeholder={placeholder}
            messageListProps={{
              style: { height: "100%", overflow: "auto", paddingBottom: "6px" },
              isDebug: true,
            }}
          />
          <Text
            type="MinimalPrint1"
            color="GrayWarm6"
            margin="10px 0 0"
            style={{ textAlign: "center" }}
          >
            AI may make mistakes.{" "}
            <a
              href="https://new-interface-531cfa.zapier.app/page"
              target="_blank"
              rel="noopener noreferrer"
            >
              Give Feedback
            </a>
          </Text>
        </div>
      </div>
      <FooterSlot />
    </div>
  );
}

const ticketCopilot = {
  id: COPILOT_ID,
  name: "Ticket Copilot",
  description: `Ticket Copilot is an internal AI assistant for Zapier Support.`,
  generateUrl: () => getTicketCopilotBaseUrl(),
};

// Workaround for AI Toolkit limitation with single copilot registries
// The dummy will never be used for actual handoffs
const dummyCopilot = {
  id: "dummy-copilot" as const,
  name: "Dummy Copilot",
  description:
    "Dummy copilot for workaround. Do not handoff to this copilot under any circumstances.",
  generateUrl: () => getTicketCopilotBaseUrl(),
};

const customRegistry = createCopilotRegistry([ticketCopilot, dummyCopilot]);

function TicketCopilotContent() {
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const ticketId = searchParams?.get("ticketId") || null;

  // Get conversationId from AI Toolkit's URL management
  const urlConversationId = useConversationId();

  // For ticket-based persistence: prefer localStorage, fallback to URL
  // For non-ticket usage: use URL conversationId from AI Toolkit
  const conversationId = ticketId
    ? getStoredConversationId(ticketId) || urlConversationId
    : urlConversationId;

  const profile = createProfileFromSession(session);

  return (
    <CopilotProvider
      api="/api/chat"
      copilotId={COPILOT_ID}
      registry={customRegistry}
      clientTools={[confirmLokiIssueTool]}
      profile={profile}
      feedbackConfig={{
        url: "/api/feedback",
        onSuccess: () => console.log("Feedback submitted successfully"),
        onError: (error) => console.error("Feedback submission failed:", error),
      }}
      getBody={() => ({ ticketId })}
      initialConversationId={conversationId}
      initialMessages={getInitialMessages(ticketId)}
      loadMessagesEndpoint="/api/conversations"
    >
      <TicketCopilotInner ticketId={ticketId} />
    </CopilotProvider>
  );
}

export default function HomePage() {
  return (
    <AuthedPage>
      <Suspense
        fallback={
          <div className="flex justify-center items-center h-screen">
            Loading Ticket Copilot...
          </div>
        }
      >
        <TicketCopilotContent />
      </Suspense>
    </AuthedPage>
  );
}
